"use client";

import Box from "@mui/material/Box";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { CLEAR_HISTORY_COMMAND, HISTORY_MERGE_TAG } from "@payloadcms/richtext-lexical/lexical";
import { HeadingNode, QuoteNode } from "@payloadcms/richtext-lexical/lexical/rich-text";
import { ListItemNode, ListNode } from "@payloadcms/richtext-lexical/lexical/list";
import { LexicalComposer } from "@payloadcms/richtext-lexical/lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@payloadcms/richtext-lexical/lexical/react/LexicalComposerContext";
import { ContentEditable } from "@payloadcms/richtext-lexical/lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@payloadcms/richtext-lexical/lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@payloadcms/richtext-lexical/lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@payloadcms/richtext-lexical/lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@payloadcms/richtext-lexical/lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@payloadcms/richtext-lexical/lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@payloadcms/richtext-lexical/lexical/react/LexicalRichTextPlugin";
import { HorizontalRuleNode } from "@payloadcms/richtext-lexical/lexical/react/LexicalHorizontalRuleNode";
import { HorizontalRulePlugin } from "@payloadcms/richtext-lexical/lexical/react/LexicalHorizontalRulePlugin";
import {
  TableCellNode,
  TableNode,
  TableRowNode,
  registerTablePlugin,
  registerTableSelectionObserver,
} from "@lexical/table";
import { isSafeFormFunnelContentHref } from "@portal/app/_common/lib/formFunnelContent";
import { pageContentSchemas } from "../../utils/validatePageContent";
import { emptyLexicalContent } from "../designHelpers";
import {
  lexicalRichTextState,
  pageLinkNodes,
  PageRichTextMediaContext,
  PageUploadNode,
  payloadRichTextState,
  registerRichTextNormalization,
} from "./RichTextNodes";
import { RichTextToolbar } from "./RichTextToolbar";
import type { PageMedia } from "../../types";

export interface RichTextFieldProps {
  id: string;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  readOnly?: boolean;
  media?: readonly PageMedia[];
}

function TableEditing() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    const removePlugin = registerTablePlugin(editor);
    const removeSelection = registerTableSelectionObserver(editor);
    return () => {
      removeSelection();
      removePlugin();
    };
  }, [editor]);
  return null;
}

function StateSync({
  value,
  onChange,
  readOnly,
  onNotice,
}: Omit<RichTextFieldProps, "id"> & { onNotice: (message: string) => void }) {
  const [editor] = useLexicalComposerContext();
  const lastValue = useRef(JSON.stringify(value));
  const lastValid = useRef(value);
  useEffect(
    () =>
      registerRichTextNormalization(
        editor,
        () => lastValid.current,
        () =>
          onNotice(
            "Unsupported rich-text content was ignored. Your previous content is still editable."
          )
      ),
    [editor, onNotice]
  );
  useEffect(() => {
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);
  useEffect(() => {
    const serialized = JSON.stringify(value);
    if (serialized === lastValue.current) return;
    lastValue.current = serialized;
    lastValid.current = value;
    onNotice("");
    editor.setEditorState(editor.parseEditorState(lexicalRichTextState(value)), {
      tag: "puck-external-value",
    });
    editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
  }, [editor, value, onNotice]);
  return (
    <OnChangePlugin
      ignoreSelectionChange
      onChange={(state, _editor, tags) => {
        if (tags.has("puck-external-value") || readOnly) return;
        const next = payloadRichTextState(state.toJSON());
        if (!pageContentSchemas.richText.safeParse({ content: next }).success) {
          onNotice(
            "Unsupported rich-text content was ignored. Your previous content is still editable."
          );
          editor.setEditorState(editor.parseEditorState(lexicalRichTextState(lastValid.current)), {
            tag: HISTORY_MERGE_TAG,
          });
          return;
        }
        const serialized = JSON.stringify(next);
        if (serialized === lastValue.current) return;
        lastValue.current = serialized;
        lastValid.current = next;
        onNotice("");
        onChange(next);
      }}
    />
  );
}

function Editor({ id, value, onChange, readOnly, media = [] }: RichTextFieldProps) {
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const handleError = useCallback(
    () => setError("This rich text could not be edited. Its saved content has been preserved."),
    []
  );
  return (
    <PageRichTextMediaContext.Provider value={media}>
      <LexicalComposer
        initialConfig={{
          namespace: `landing-page-${id}`,
          editable: !readOnly,
          editorState: lexicalRichTextState(value),
          nodes: [
            HeadingNode,
            QuoteNode,
            ListNode,
            ListItemNode,
            HorizontalRuleNode,
            TableNode,
            TableRowNode,
            TableCellNode,
            PageUploadNode,
            ...pageLinkNodes,
          ],
          theme: {
            text: {
              bold: "lp-rich-text-bold",
              italic: "lp-rich-text-italic",
              underline: "lp-rich-text-underline",
              strikethrough: "lp-rich-text-strike",
              underlineStrikethrough: "lp-rich-text-underline-strike",
              code: "lp-rich-text-code",
              subscript: "lp-rich-text-subscript",
              superscript: "lp-rich-text-superscript",
            },
          },
          onError: handleError,
        }}
      >
        {error ? (
          <p role="alert">{error}</p>
        ) : (
          <>
            <RichTextToolbar id={id} readOnly={readOnly} />
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  id={id}
                  aria-label="Rich text content"
                  className="lp-rich-text-input"
                />
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <ListPlugin hasStrictIndent />
            <LinkPlugin validateUrl={isSafeFormFunnelContentHref} />
            <HorizontalRulePlugin />
            <TableEditing />
            <StateSync value={value} onChange={onChange} readOnly={readOnly} onNotice={setNotice} />
            {notice && <p role="alert">{notice}</p>}
          </>
        )}
      </LexicalComposer>
    </PageRichTextMediaContext.Provider>
  );
}

/** Edits the existing Lexical document directly; formatting survives save and reload. */
export function RichTextField(props: RichTextFieldProps) {
  // Puck mounts hidden and visible copies of the same field at once.
  const instanceId = useId();
  const domId = `${props.id}-${instanceId}`;
  const valid = pageContentSchemas.richText.safeParse({ content: props.value }).success;
  const root = props.value.root as { children?: unknown[] } | undefined;
  const value = valid && root?.children?.length === 0 ? emptyLexicalContent("") : props.value;
  return (
    <Box
      sx={{
        border: "1px solid #cbd2df",
        borderRadius: "8px",
        bgcolor: "#fff",
        color: "#151e39",
        overflow: "hidden",
        "& button": {
          font: "inherit",
          fontSize: 12,
          minWidth: 28,
          minHeight: 28,
          border: "1px solid #cbd2df",
          borderRadius: "4px",
          bgcolor: "#fff",
          color: "#151e39",
          cursor: "pointer",
          px: "6px",
          "&[aria-pressed=true]": { bgcolor: "#e5ebff", borderColor: "#173bb0" },
          "&:disabled": { opacity: 0.45, cursor: "default" },
        },
        "& .lp-rich-text-toolbar": {
          display: "flex",
          flexWrap: "wrap",
          gap: "4px",
          p: "6px",
          borderBottom: "1px solid #e2e6ef",
        },
        "& .lp-rich-text-input": {
          minHeight: 160,
          maxHeight: 420,
          overflowY: "auto",
          p: "10px",
          fontSize: 14,
          lineHeight: 1.6,
          outlineOffset: -2,
          "& p": { m: "0 0 8px" },
          "& h1, & h2, & h3, & h4, & h5, & h6": { m: "12px 0 8px", lineHeight: 1.25 },
          "& h2": { fontSize: 22 },
          "& h3": { fontSize: 18 },
          "& ul, & ol": { pl: "24px", m: "0 0 8px" },
          "& ul": { listStyleType: "disc" },
          "& ol": { listStyleType: "decimal" },
          "& blockquote": { borderLeft: "3px solid #cbd2df", pl: "12px", ml: 0 },
          "& a": { color: "#173bb0", textDecoration: "underline" },
          "& table": { borderCollapse: "collapse", maxWidth: "100%" },
          "& td, & th": { border: "1px solid #cbd2df", p: "6px", minWidth: 40 },
          "& th": { bgcolor: "#f0f2f7", fontWeight: 700 },
        },
        "& .lp-rich-text-bold": { fontWeight: 700 },
        "& .lp-rich-text-italic": { fontStyle: "italic" },
        "& .lp-rich-text-underline": { textDecoration: "underline" },
        "& .lp-rich-text-strike": { textDecoration: "line-through" },
        "& .lp-rich-text-underline-strike": { textDecoration: "underline line-through" },
        "& .lp-rich-text-code": { fontFamily: "monospace", bgcolor: "#f0f2f7", px: "2px" },
        "& .lp-rich-text-subscript": { fontSize: "0.8em", verticalAlign: "sub" },
        "& .lp-rich-text-superscript": { fontSize: "0.8em", verticalAlign: "super" },
        "& .lp-rich-text-link-controls": {
          display: "grid",
          gap: "6px",
          p: "8px",
          bgcolor: "#f4f6fc",
          fontSize: 13,
          "& input[type=text]": { minWidth: 0, width: "100%", boxSizing: "border-box", p: "6px" },
          "& > div": { display: "flex", flexWrap: "wrap", gap: "4px" },
        },
        "& p[role=alert]": { m: "8px", color: "#a12622", fontSize: 13 },
      }}
    >
      {valid ? (
        <Editor key={props.id} {...props} id={domId} value={value} />
      ) : (
        <p role="alert">
          This content uses unsupported rich-text features. Its saved content has been preserved.
        </p>
      )}
    </Box>
  );
}
